import { useMemo } from "react";
import "./MindMappingReportASixthSection.css";

/* ── MBTI letter map ──────────────────────────────────────────── */
const TRAIT_LETTER = {
  Extravert: "E", Introvert: "I",
  Thinking: "T",  Feeling: "F",
  Judging: "J",   Perceiving: "P",
  Intuition: "N", Sensing: "S",
};

/* ── Deep investor / founder perspective per trait value ─────── */
const TRAIT_INSIGHT = {
  Extravert:   "Extroverted founders build investor relationships faster — leverage networking events, demo days, and warm introductions to accelerate fundraising timelines.",
  Introvert:   "Introverted founders produce deeper, research-backed products — channel this into thorough market research, product roadmaps, and written content marketing.",
  Thinking:    "Analytical decision-makers excel at unit economics and systems design — use this strength when pitching operational efficiency and scalability to investors.",
  Feeling:     "Empathy-driven founders build higher customer retention — highlight user-centricity and community loyalty as a competitive moat in investor conversations.",
  Judging:     "Structured executors close milestones on time — use this trait to demonstrate strong project management and execution credibility in your pitch deck.",
  Perceiving:  "Adaptive, pivot-ready founders absorb market feedback efficiently — position yourself as hypothesis-driven, emphasizing your rapid iteration capability.",
  Intuition:   "Vision-led founders identify market opportunities others miss — back your insight with data to bridge the gap between vision and investor conviction.",
  Sensing:     "Detail-oriented, reality-grounded founders build operationally sound startups — use your precision to validate assumptions with real-world evidence.",
};

/* ── MBTI type archetypes ──────────────────────────────────────── */
const MBTI_PROFILES = {
  INTJ: { name: "The Architect",     fit: "Deep tech, platform businesses, long-cycle B2B SaaS" },
  INTP: { name: "The Logician",      fit: "Research-intensive products, developer tools, AI/ML startups" },
  ENTJ: { name: "The Commander",     fit: "Operations-heavy startups, enterprise SaaS, market leadership plays" },
  ENTP: { name: "The Debater",       fit: "Consumer internet, marketplace disruption, creative tech sectors" },
  INFJ: { name: "The Advocate",      fit: "Social impact, EdTech, mental health, mission-driven ventures" },
  INFP: { name: "The Mediator",      fit: "Creative platforms, wellness brands, community-first products" },
  ENFJ: { name: "The Protagonist",   fit: "Consumer brands, community platforms, social-driven startups" },
  ENFP: { name: "The Campaigner",    fit: "Consumer apps, media, creator economy, viral growth products" },
  ISTJ: { name: "The Logistician",   fit: "Fintech, compliance-tech, supply chain, B2B operations tools" },
  ISFJ: { name: "The Defender",      fit: "Healthcare support, childcare tech, education services" },
  ESTJ: { name: "The Executive",     fit: "Enterprise ops, manufacturing tech, process automation SaaS" },
  ESFJ: { name: "The Consul",        fit: "Retail tech, D2C services, healthcare, community ventures" },
  ISTP: { name: "The Virtuoso",      fit: "Hardware, manufacturing, deep engineering, IoT startups" },
  ISFP: { name: "The Adventurer",    fit: "D2C lifestyle brands, artisan tech, food & wellness products" },
  ESTP: { name: "The Entrepreneur",  fit: "Sales-led growth, growth hacking, high-velocity consumer ventures" },
  ESFP: { name: "The Entertainer",   fit: "Events tech, entertainment, experiential consumer products" },
};

const MindMappingReportASixthSection = ({ res, toggle }) => {
  const traits = useMemo(() => {
    if (!res) return [];
    return [
      { sno: "6 A", label: "Energy Source",       val: res.trait_intro_extro   },
      { sno: "6 B", label: "Decision Making",     val: res.trait_think_feel    },
      { sno: "6 C", label: "Execution Style",     val: res.trait_judge_perceive },
      { sno: "6 D", label: "Information Intake",  val: res.trait_sense_intuit  },
    ];
  }, [res]);

  const mbtiCode = useMemo(() => {
    const e = TRAIT_LETTER[res?.trait_intro_extro]   ?? "?";
    const t = TRAIT_LETTER[res?.trait_sense_intuit]  ?? "?";
    const f = TRAIT_LETTER[res?.trait_think_feel]    ?? "?";
    const j = TRAIT_LETTER[res?.trait_judge_perceive] ?? "?";
    return `${e}${t}${f}${j}`;
  }, [res]);

  const profile = MBTI_PROFILES[mbtiCode];
  const profileName = profile ? `${mbtiCode} — ${profile.name}` : mbtiCode;
  const profileFit  = profile?.fit ?? "Versatile across multiple startup categories — see trait-level insights above for specific strengths.";

  return (
    <div className="mind-mapping-report-a-sixth-container">
      <div className={toggle ? "table-adjust" : "mind-mapping-report-a-sixth-final-table"}>
        <span className="mind-mapping-report-a-sixth-corner mind-mapping-report-a-sixth-corner-top-left" />
        <span className="mind-mapping-report-a-sixth-corner mind-mapping-report-a-sixth-corner-top-right" />
        <span className="mind-mapping-report-a-sixth-corner mind-mapping-report-a-sixth-corner-bottom-left" />
        <span className="mind-mapping-report-a-sixth-corner mind-mapping-report-a-sixth-corner-bottom-right" />

        <div className="mind-mapping-report-a-sixth-module-title">
          <span className="module-prefix">Module 6:</span> Personality Profile &amp; Founder Archetype
        </div>

        <div className="mind-mapping-report-a-sixth-table-scroll-wrapper">
          <table className="mind-mapping-report-a-sixth-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-a-sixth-col-sno">S.NO</th>
                <th>Trait Dimension</th>
                <th>Your Profile</th>
                <th>Founder / Investor Signal</th>
              </tr>
            </thead>
            <tbody>
              {traits.map((item, index) => (
                <tr key={index}>
                  <td className="mind-mapping-report-a-sixth-col-sno">{item.sno}</td>
                  <td>{item.label}</td>
                  <td className="mind-mapping-report-a-sixth-user-val">{item.val || "N/A"}</td>
                  <td className="mind-mapping-report-a-sixth-highlight">
                    {TRAIT_INSIGHT[item.val] ?? "Balanced trait — adaptable across multiple startup contexts."}
                  </td>
                </tr>
              ))}

              {/* Synthesised MBTI row */}
              <tr className="mbti-synthesis-row">
                <td className="mind-mapping-report-a-sixth-col-sno">6 E</td>
                <td><strong>Founder Archetype</strong></td>
                <td className="mind-mapping-report-a-sixth-user-val">
                  <strong>{profileName}</strong>
                </td>
                <td className="mind-mapping-report-a-sixth-highlight">
                  <strong>Best-fit startup types: </strong>{profileFit}
                </td>
              </tr>

              {/* Startup style recommendation row */}
              <tr>
                <td className="mind-mapping-report-a-sixth-col-sno">6 F</td>
                <td>Startup Style Fit</td>
                <td className="mind-mapping-report-a-sixth-user-val">
                  {res?.sector ? `${res.sector} (${res.sector_type ?? "Tertiary"})` : "Sector not specified"}
                </td>
                <td className="mind-mapping-report-a-sixth-highlight">
                  {mbtiCode.startsWith("E")
                    ? `Your extroverted energy gives a GTM edge in the ${res?.sector ?? "chosen"} sector — prioritise customer-facing traction early.`
                    : `Your introverted depth suits a product-led growth strategy in the ${res?.sector ?? "chosen"} sector — invest in quality and word-of-mouth loops.`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportASixthSection;
